import React, { useMemo, useState } from 'react';
import { Check, ChevronDown, ClipboardList, Loader2, Sparkles, X } from 'lucide-react';
import api from '../../services/api';
import { QuizContent } from './QuizContent';
import type { QuizQuestion } from '../../hooks/useCourseApi';
import { cardTheme } from '../../styles/cardTheme';

export interface AvailableQuizOption {
  id: number;
  lessonName: string;
  unitName: string;
}

interface ConfiguredPracticeQuizProps {
  quizzes: AvailableQuizOption[];
}

type Phase = 'setup' | 'loading' | 'taking';

function shuffle<T>(items: T[]): T[] {
  const next = [...items];
  for (let i = next.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [next[i], next[j]] = [next[j], next[i]];
  }
  return next;
}

function normalizeQuestions(raw: unknown): QuizQuestion[] {
  if (!Array.isArray(raw)) return [];

  return raw
    .map((item): QuizQuestion | null => {
      if (!item || typeof item !== 'object') return null;
      const q = item as Record<string, unknown>;
      const text = String(q.text ?? q.Text ?? '').trim();
      if (!text) return null;

      const rawAnswers = (q.answers ?? q.Answers) as unknown;
      if (!Array.isArray(rawAnswers) || rawAnswers.length === 0) return null;

      const answers = rawAnswers.map((a) => {
        const answer = (a && typeof a === 'object' ? a : {}) as Record<string, unknown>;
        return {
          text: String(answer.text ?? answer.Text ?? ''),
          isCorrect: Boolean(answer.isCorrect ?? answer.IsCorrect),
        };
      });

      const typeRaw = String(q.type ?? q.Type ?? 'MultipleChoice').toLowerCase();
      const type: QuizQuestion['type'] =
        typeRaw.includes('true') || typeRaw === '2' || typeRaw === '1'
          ? 'TrueFalse'
          : 'MultipleChoice';

      return { text, type, answers };
    })
    .filter((q): q is QuizQuestion => q != null);
}

export const ConfiguredPracticeQuiz: React.FC<ConfiguredPracticeQuizProps> = ({ quizzes }) => {
  const [phase, setPhase] = useState<Phase>('setup');
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [questionCount, setQuestionCount] = useState(10);
  const [practiceQuestions, setPracticeQuestions] = useState<QuizQuestion[]>([]);
  const [sessionKey, setSessionKey] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [isOpen, setIsOpen] = useState(false);

  const selectedQuizzes = useMemo(
    () => quizzes.filter((q) => selectedIds.includes(q.id)),
    [quizzes, selectedIds]
  );

  const toggleQuiz = (id: number) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
    setError(null);
  };

  const resetToSetup = () => {
    setPhase('setup');
    setPracticeQuestions([]);
    setError(null);
  };

  const closePanel = () => {
    setIsOpen(false);
    resetToSetup();
    setSelectedIds([]);
    setQuestionCount(10);
  };

  const startPractice = async () => {
    if (selectedIds.length === 0) {
      setError('اختر اختباراً واحداً على الأقل');
      return;
    }

    const count = Math.floor(Number(questionCount));
    if (!Number.isFinite(count) || count < 1) {
      setError('أدخل عدد أسئلة صالحاً (١ فأكثر)');
      return;
    }

    setPhase('loading');
    setError(null);

    try {
      const pools = await Promise.all(
        selectedIds.map(async (lessonId) => {
          const response = await api.get<{
            success?: boolean;
            message?: string;
            data?: { questions?: unknown };
          }>(`/api/Student/contentlesson/${lessonId}`);

          const body = response.data;
          if (!body?.success || !body.data) {
            throw new Error(body?.message || 'تعذّر تحميل أحد الاختبارات');
          }
          return normalizeQuestions(body.data.questions);
        })
      );

      const pool = pools.flat();
      if (pool.length === 0) {
        setError('لا توجد أسئلة في الاختبارات المختارة');
        setPhase('setup');
        return;
      }

      const take = Math.min(count, pool.length);
      const picked = shuffle(pool).slice(0, take);
      setPracticeQuestions(picked);
      setSessionKey((k) => k + 1);
      setPhase('taking');

      if (count > pool.length) {
        setError(`متاح ${pool.length} سؤالاً فقط — تم إنشاء اختبار بـ ${pool.length} أسئلة`);
      }
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : 'حدث خطأ أثناء تجهيز الاختبار';
      setError(message);
      setPhase('setup');
    }
  };

  if (quizzes.length === 0) {
    return (
      <section
        className={`${cardTheme.shell} ${cardTheme.accentTop} mb-5 p-4 sm:mb-7 sm:p-5`}
        aria-labelledby="practice-quiz-heading"
      >
        <div className="flex items-start gap-3">
          <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${cardTheme.iconBoxGold}`}>
            <ClipboardList className="h-5 w-5" />
          </div>
          <div className="min-w-0">
            <h2 id="practice-quiz-heading" className={`text-base font-bold sm:text-lg ${cardTheme.title}`}>
              اعمل اختبارك بنفسك
            </h2>
            <p className={`mt-1 text-sm ${cardTheme.subtitle}`}>
              لا توجد اختبارات في هذا المنهج حالياً.
            </p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section
      className={`${cardTheme.shell} ${cardTheme.accentTop} mb-5 overflow-hidden sm:mb-7 ${!isOpen ? cardTheme.shellHover : ''}`}
      aria-labelledby="practice-quiz-heading"
    >
      <button
        type="button"
        onClick={() => (isOpen ? closePanel() : setIsOpen(true))}
        className="flex w-full min-h-14 items-center gap-3 bg-gradient-to-l from-gold-50/50 via-white to-green-50/40 p-3.5 text-right sm:gap-4 sm:p-4"
        aria-expanded={isOpen}
      >
        <div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl sm:h-12 sm:w-12 ${cardTheme.iconBoxGold}`}>
          <Sparkles className="h-5 w-5" />
        </div>
        <div className="min-w-0 flex-1">
          <h2 id="practice-quiz-heading" className={`text-sm font-bold sm:text-base ${cardTheme.title}`}>
            اعمل اختبارك بنفسك
          </h2>
          <p className={`mt-0.5 text-xs sm:text-sm ${cardTheme.subtitle}`}>
            اختر اختبارات وحدد عدد الأسئلة · {quizzes.length} متاح
          </p>
        </div>
        <span
          className={`inline-flex min-h-9 shrink-0 items-center gap-1 rounded-full px-3 text-xs font-bold sm:min-h-10 sm:text-sm ${
            isOpen ? cardTheme.btnOutline : cardTheme.btnGold
          } ${isOpen ? 'border px-3' : 'px-3.5'}`}
        >
          {isOpen ? 'إغلاق' : 'ابدأ'}
          <ChevronDown className={`h-4 w-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </span>
      </button>

      {isOpen && (
        <div className="border-t border-green-100 bg-white p-3.5 sm:p-5">
          {phase === 'taking' ? (
            <div className="space-y-3">
              {error && (
                <div className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2.5 text-xs text-amber-900 sm:text-sm">
                  {error}
                </div>
              )}
              <QuizContent
                key={sessionKey}
                questions={practiceQuestions}
                practiceMode
                onComplete={resetToSetup}
              />
              <button
                type="button"
                onClick={resetToSetup}
                className={`inline-flex min-h-11 w-full items-center justify-center rounded-xl px-4 text-sm font-semibold sm:w-auto ${cardTheme.btnOutline}`}
              >
                إعداد اختبار جديد
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              <div>
                <div className="mb-2 flex items-center justify-between gap-2">
                  <p className={`text-sm font-bold ${cardTheme.title}`}>الاختبارات</p>
                  <span className={cardTheme.badgeGold}>
                    {selectedIds.length}/{quizzes.length}
                  </span>
                </div>
                <ul className="max-h-52 space-y-2 overflow-y-auto sm:max-h-64">
                  {quizzes.map((quiz) => {
                    const checked = selectedIds.includes(quiz.id);
                    return (
                      <li key={quiz.id}>
                        <button
                          type="button"
                          onClick={() => toggleQuiz(quiz.id)}
                          aria-pressed={checked}
                          className={`flex w-full min-h-12 items-start gap-3 rounded-xl border px-3 py-2.5 text-right transition sm:items-center sm:px-3.5 ${
                            checked
                              ? 'border-gold-400 bg-gold-50/70'
                              : 'border-green-100 bg-green-50/30 hover:border-green-300 hover:bg-green-50/60'
                          }`}
                        >
                          <span
                            className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-md sm:mt-0 ${
                              checked
                                ? 'bg-gold-500 text-green-950'
                                : 'border border-green-200 bg-white'
                            }`}
                          >
                            {checked && <Check className="h-3.5 w-3.5" strokeWidth={3} />}
                          </span>
                          <span className="min-w-0 flex-1">
                            <span className="block break-words text-sm font-bold text-green-900">
                              {quiz.lessonName}
                            </span>
                            <span className="mt-0.5 block text-[11px] text-gray-500 sm:text-xs">
                              {quiz.unitName}
                            </span>
                          </span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>

              <div>
                <label
                  htmlFor="practice-question-count"
                  className={`mb-1.5 block text-sm font-bold ${cardTheme.title}`}
                >
                  عدد الأسئلة
                </label>
                <div className="flex flex-wrap items-center gap-2">
                  {[5, 10, 15, 20].map((n) => (
                    <button
                      key={n}
                      type="button"
                      onClick={() => {
                        setQuestionCount(n);
                        setError(null);
                      }}
                      className={`inline-flex min-h-10 min-w-10 items-center justify-center rounded-xl text-sm font-bold transition ${
                        questionCount === n
                          ? 'bg-green-700 text-white'
                          : 'border border-green-200 bg-white text-green-800 hover:border-gold-400 hover:bg-gold-50'
                      }`}
                    >
                      {n}
                    </button>
                  ))}
                  <input
                    id="practice-question-count"
                    type="number"
                    min={1}
                    inputMode="numeric"
                    value={questionCount}
                    onChange={(e) => {
                      setQuestionCount(Number(e.target.value));
                      setError(null);
                    }}
                    className="min-h-10 w-20 rounded-xl border border-green-200 bg-white px-2 text-center text-sm font-bold text-green-900 outline-none focus:border-gold-400 focus:ring-2 focus:ring-gold-200"
                  />
                </div>
              </div>

              {error && (
                <div className="flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 px-3 py-2.5 text-sm text-red-700">
                  <X className="mt-0.5 h-4 w-4 shrink-0" />
                  <span className="min-w-0 flex-1 break-words">{error}</span>
                </div>
              )}

              <button
                type="button"
                onClick={startPractice}
                disabled={phase === 'loading' || selectedIds.length === 0}
                className={`inline-flex min-h-12 w-full items-center justify-center gap-2 rounded-xl px-5 text-sm font-bold sm:text-base disabled:cursor-not-allowed disabled:opacity-50 ${cardTheme.btnGold}`}
              >
                {phase === 'loading' ? (
                  <>
                    <Loader2 className="h-5 w-5 animate-spin" />
                    جاري التجهيز...
                  </>
                ) : (
                  <>
                    <Sparkles className="h-4 w-4" />
                    ابدأ التدريب
                    {selectedQuizzes.length > 0 ? ` · ${questionCount} سؤال` : ''}
                  </>
                )}
              </button>
            </div>
          )}
        </div>
      )}
    </section>
  );
};

export default ConfiguredPracticeQuiz;
